import React, { useContext } from "react";
import { WplywContext } from "../../../../../../../context/wplywContext";
import { Input, Button, Select } from "antd";
import CalculatorInput from "../../../../../../../components/CalculatorInput";
import LineSpacer from "../../../../../../../components/LineSpacer";
import { DeleteOutlined } from "@ant-design/icons";
import { useAuth } from "../../../../../../../context/AuthContext";
import Label from "../../../../../../../components/Label";
import FixedNumberInput from "../../../../../../../components/FixedNumberInput";

const currencyFormatter = new Intl.NumberFormat("pl-PL", {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

const percentFormatter = new Intl.NumberFormat("pl-PL", {
	minimumFractionDigits: 0,
	maximumFractionDigits: 2,
});

const normalizeNumber = (value) => {
	const parsedValue = Number(value);
	return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getLoanRepaymentPercentage = (loanAmount, remainingAmount) => {
	if (loanAmount <= 0) {
		return 0;
	}

	const repaidAmount = Math.max(0, loanAmount - remainingAmount);
	return Math.min(100, (repaidAmount / loanAmount) * 100);
};

const getLoanOptionLabel = (loan) => {
	const loanAmount = normalizeNumber(loan?.msr_loans_amount);
	const remainingAmount = normalizeNumber(loan?.msr_loans_remaining_amount);
	const repaidPercentage = getLoanRepaymentPercentage(
		loanAmount,
		remainingAmount,
	);

	return `${loan?.label || String(loan?.value || "")} | kwota ${currencyFormatter.format(
		loanAmount,
	)} zł | spłacono ${percentFormatter.format(repaidPercentage)}%`;
};

const ReckoningsDeduction = ({
	selectedPerson,
	deduction,
	index,
	preindex,
	reckoning,
}) => {
	const { wplyw, setWplyw } = useContext(WplywContext);
	const { user } = useAuth();
	const isGeneratedTransferLocked =
		Boolean(selectedPerson?.msr_persons_transfers_generated_file) ||
		Boolean(selectedPerson?.msr_reserves_generated_file) ||
		Boolean(
			(selectedPerson?.msr_transfers || []).some(
				(t) => t?.msr_transfers_generated_file,
			),
		);
	const isDisabled = wplyw.mode === "preview" || isGeneratedTransferLocked;
	const isLoanDeductionLocked =
		Boolean(deduction?.msr_loans_id) &&
		Boolean(deduction?.msr_deductions_loan_confirmed);

	const deductionTypes =
		wplyw?.wplyw_data?.data?.default_values?.msr_deduction_types || [];
	const deductionTypeId =
		deduction.msr_deduction_types_id || deductionTypes?.[0]?.value;
	const isLoanDeductionType = (() => {
		const dt = deductionTypes.find(
			(x) => String(x.value) === String(deductionTypeId),
		);
		const name = String(
			dt?.msr_deduction_types_name ?? dt?.label ?? "",
		).toLowerCase();
		return name.includes("pożycz") || name.includes("pozycz");
	})();

	const loansRaw = wplyw?.wplyw_data?.data?.default_values?.msr_loans || [];
	const loans = [...loansRaw].sort((a, b) => {
		const aDate = Number(a?.msr_loans_date ?? 0);
		const bDate = Number(b?.msr_loans_date ?? 0);
		if (aDate !== bDate) return aDate - bDate;
		return (
			Number(a?.msr_loans_id ?? a?.value ?? 0) -
			Number(b?.msr_loans_id ?? b?.value ?? 0)
		);
	});

	const borrowerOptionsRaw =
		wplyw?.wplyw_data?.data?.default_values?.msr_loan_borrowers || [];
	const borrowerOptions = (() => {
		const byId = new Map();
		for (const loan of loans) {
			const id = loan?.msr_loan_borrowers_id;
			if (!id) continue;
			byId.set(String(id), {
				value: id,
				label: loan?.msr_loan_borrowers_label || String(id),
			});
		}
		// Fallback: if there are no loans in default values, keep all borrowers.
		if (byId.size === 0) {
			return borrowerOptionsRaw;
		}
		return Array.from(byId.values()).sort((a, b) =>
			String(a?.label || "").localeCompare(String(b?.label || "")),
		);
	})();
	const selectedLoan = loans.find(
		(x) => String(x.value) === String(deduction?.msr_loans_id),
	);

	const selectedBorrowerId = deduction?.msr_loan_borrowers_id || undefined;
	const effectiveBorrowerId =
		selectedBorrowerId || selectedLoan?.msr_loan_borrowers_id || undefined;
	const filteredLoans = (
		effectiveBorrowerId
			? loans.filter(
					(x) =>
						String(x?.msr_loan_borrowers_id) ===
						String(effectiveBorrowerId),
				)
			: loans
	)
		.filter((loan) => {
			const remainingAmount = normalizeNumber(
				loan?.msr_loans_remaining_amount,
			);
			return (
				remainingAmount > 0 ||
				String(loan?.value) === String(deduction?.msr_loans_id)
			);
		})
		.map((loan) => ({
			...loan,
			label: getLoanOptionLabel(loan),
		}));

	const getAvailableDeductionAmount = () => {
		const otherDeductionsSum = (reckoning?.msr_deductions || []).reduce(
			(sum, ded) =>
				sum +
				(ded.msr_deductions_id != deduction.msr_deductions_id
					? parseFloat(ded.msr_deductions_amount)
					: 0),
			0,
		);
		return (
			reckoning.msr_reckonings_part_amount -
			reckoning.msr_reckonings_deduction_from_income -
			otherDeductionsSum
		);
	};

	const labelStyle = {
		color: "black",
		fontWeight: "500",
		fontSize: "16px",
		marginBottom: "8px",
	};

	const checkValues = (field, value) => {
		switch (field) {
			case "msr_deduction_types_id":
				return (
					deductionTypes?.find((x) => x.value == value)?.label ||
					value
				);
			case "msr_loans_id":
				return (
					filteredLoans.find((x) => x.value == value)?.label ||
					getLoanOptionLabel(
						loans.find((x) => x.value == value) || {},
					) ||
					value
				);
			case "msr_loan_borrowers_id":
				return (
					borrowerOptionsRaw.find((x) => x.value == value)?.label ||
					borrowerOptions.find((x) => x.value == value)?.label ||
					value
				);
			default:
				return value; // Return the value unchanged if no specific case is matched
		}
	};

	// Update deduction field in context
	const updateReckoningsDeductionField = (field, value) => {
		const fieldLabels = {
			msr_deductions_title: "Tytuł odliczenia",
			msr_deductions_comment: "Komentarz odliczenia",
			msr_deductions_amount: "Kwota odliczenia",
			msr_deduction_types_id: "Typ odliczenia",
			msr_loan_borrowers_id: "Pożyczkobiorca",
			msr_loans_id: "Pożyczka",
		};

		setWplyw((prevData) => {
			const updatedPersons =
				prevData.wplyw_data.data.msr_wplywy.msr_persons.map(
					(person) => {
						if (
							person.msr_persons_id ===
							selectedPerson.msr_persons_id
						) {
							return {
								...person,
								msr_reckonings: person.msr_reckonings.map(
									(reckoning) => {
										if (
											reckoning.msr_reckonings_id ===
											deduction.msr_reckonings_id
										) {
											return {
												...reckoning,
												msr_deductions:
													reckoning.msr_deductions.map(
														(deductionItem) => {
															if (
																deductionItem.msr_deductions_id ===
																deduction.msr_deductions_id
															) {
																return {
																	...deductionItem,
																	[field]:
																		value,
																};
															}
															return deductionItem;
														},
													),
											};
										}
										return reckoning;
									},
								),
							};
						}
						return person;
					},
				);

			// Handle changes array
			const changes = [...(prevData.changes || [])];
			const existingChangeIndex = changes.findIndex(
				(change) =>
					change.field === field &&
					change.objectid === deduction.msr_deductions_own_id,
			);

			const originalValue = deduction[field];

			if (existingChangeIndex > -1) {
				// Update existing log if it exists
				changes[existingChangeIndex] = {
					...changes[existingChangeIndex],
					newValue:
						field == "msr_deduction_types_id"
							? checkValues("msr_deduction_types_id", value)
							: value,
					datetime: Math.floor(new Date().getTime() / 1000),
				};
			} else {
				// Add new log if it doesn't exist and value is changed
				changes.push({
					etap: "Odliczenie",
					field: field,
					label: fieldLabels[field] || field,
					action: "edit",
					oldValue: originalValue,
					newValue:
						field == "msr_deduction_types_id"
							? checkValues("msr_deduction_types_id", value)
							: field == "msr_loans_id"
								? checkValues("msr_loans_id", value)
								: field == "msr_loan_borrowers_id"
									? checkValues(
											"msr_loan_borrowers_id",
											value,
										)
									: value,
					datetime: Math.floor(new Date().getTime() / 1000),
					user: user.user.mlg_users_id,
					objectid: deduction.msr_deductions_own_id,
					wplywid:
						prevData.wplyw_data.data.msr_wplywy.msr_wplywy_own_id,
				});
			}

			return {
				...prevData,
				wplyw_data: {
					...prevData.wplyw_data,
					data: {
						...prevData.wplyw_data.data,
						msr_wplywy: {
							...prevData.wplyw_data.data.msr_wplywy,
							msr_persons: updatedPersons,
						},
					},
				},
				changes,
			};
		});
	};

	// Remove deduction
	const removeDeduction = () => {
		setWplyw((prevData) => {
			const updatedPersons =
				prevData.wplyw_data.data.msr_wplywy.msr_persons.map(
					(person) => {
						if (
							person.msr_persons_id ===
							selectedPerson.msr_persons_id
						) {
							return {
								...person,
								msr_reckonings: person.msr_reckonings.map(
									(reckoning) => {
										if (
											reckoning.msr_reckonings_id ===
											deduction.msr_reckonings_id
										) {
											return {
												...reckoning,
												msr_deductions:
													reckoning.msr_deductions.filter(
														(deductionItem) =>
															deductionItem.msr_deductions_id !==
															deduction.msr_deductions_id,
													),
											};
										}
										return reckoning;
									},
								),
							};
						}
						return person;
					},
				);

			// Add log entry for removing the deduction
			const changes = [...(prevData.changes || [])];
			changes.push({
				etap: "Odliczenie",
				field: "msr_deductions",
				label: "Usunięto odliczenie",
				action: "remove",
				oldValue: `Usunięto odliczenie o ID ${deduction.msr_deductions_own_id}`,
				newValue: null,
				datetime: Math.floor(new Date().getTime() / 1000),
				user: user.user.mlg_users_id,
				objectid: deduction.msr_deductions_own_id,
				wplywid: prevData.wplyw_data.data.msr_wplywy.msr_wplywy_own_id,
				personid: selectedPerson.msr_persons_own_id,
			});

			return {
				...prevData,
				wplyw_data: {
					...prevData.wplyw_data,
					data: {
						...prevData.wplyw_data.data,
						msr_wplywy: {
							...prevData.wplyw_data.data.msr_wplywy,
							msr_persons: updatedPersons,
						},
					},
				},
				changes, // Update the changes array
			};
		});
	};

	return (
		<div style={{ marginTop: "16px" }}>
			<div
				style={{
					padding: "20px",
					borderRadius: "10px",
					background: "#F9F9F9",
					display: "grid",
					gap: "24px",
				}}
			>
				<div
					style={{
						display: "flex",
						gap: "20px",
						alignItems: "center",
					}}
				>
					<div
						style={{
							background: "white",
							color: "black",
							border: "1px solid black",
							padding: "7px 15px",
							fontSize: "16px",
							width: "fit-content",
							borderRadius: "5px",
						}}
					>
						#{preindex + 1}.{index + 1}
					</div>
					<LineSpacer />
					{!isDisabled && (
						<Button
							disabled={isLoanDeductionLocked}
							onClick={removeDeduction}
							type="ghost"
							icon={<DeleteOutlined></DeleteOutlined>}
						>
							Usuń odliczenie
						</Button>
					)}
				</div>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(4, 1fr)",
						alignItems: "end",
						gap: "32px",
					}}
				>
					<div>
						<Label required>Kwota odliczenia</Label>
						<FixedNumberInput
							mode={wplyw.mode}
							disabled={isDisabled || isLoanDeductionLocked}
							calculator={true}
							maxvalue={
								reckoning.msr_reckonings_part_amount -
								reckoning.msr_reckonings_deduction_from_income -
								reckoning.msr_deductions.reduce(
									(sum, ded) =>
										sum +
										(ded.msr_deductions_id !=
										deduction.msr_deductions_id
											? parseFloat(
													ded.msr_deductions_amount,
												)
											: 0),
									0,
								)
							}
							value={deduction.msr_deductions_amount}
							addonAfter="zł"
							onChange={(value) =>
								updateReckoningsDeductionField(
									"msr_deductions_amount",
									value,
								)
							}
							status={
								deduction.msr_deductions_amount &&
								deduction.msr_deductions_amount > 0
									? "default"
									: "error"
							}
						/>
					</div>
					<div>
						<Label required>Tytuł odliczenia</Label>
						<Input
							disabled={isDisabled || isLoanDeductionLocked}
							value={deduction.msr_deductions_title || ""}
							onChange={(e) =>
								updateReckoningsDeductionField(
									"msr_deductions_title",
									e.target.value,
								)
							}
							status={
								deduction.msr_deductions_title
									? "default"
									: "error"
							}
						/>
					</div>
					<div>
						<div style={labelStyle}>Typ odliczenia</div>
						<Select
							style={{ width: "100%" }}
							disabled={isDisabled || isLoanDeductionLocked}
							value={deductionTypeId}
							defaultValue={deductionTypes?.[0]?.value}
							options={deductionTypes || []}
							onChange={(value) => {
								const prevIsLoanType = isLoanDeductionType;
								const nextType = deductionTypes.find(
									(x) => String(x.value) === String(value),
								);
								const nextName = String(
									nextType?.msr_deduction_types_name ??
										nextType?.label ??
										"",
								).toLowerCase();
								const nextIsLoanType =
									nextName.includes("pożycz") ||
									nextName.includes("pozycz");

								updateReckoningsDeductionField(
									"msr_deduction_types_id",
									value,
								);

								if (prevIsLoanType && !nextIsLoanType) {
									updateReckoningsDeductionField(
										"msr_loans_id",
										null,
									);
									updateReckoningsDeductionField(
										"msr_loan_borrowers_id",
										null,
									);
								}
							}}
						/>
					</div>
					{isLoanDeductionType ? (
						<>
							<div style={{ gridColumn: "span 2" }}>
								<Label>Pożyczkobiorca</Label>
								<Select
									style={{ width: "100%" }}
									disabled={
										isDisabled || isLoanDeductionLocked
									}
									showSearch
									optionFilterProp="label"
									filterOption={(input, option) =>
										String(option?.label || "")
											.toLowerCase()
											.includes(
												String(
													input || "",
												).toLowerCase(),
											)
									}
									allowClear
									placeholder="Wybierz osobę"
									value={effectiveBorrowerId}
									options={borrowerOptions}
									onChange={(value) => {
										updateReckoningsDeductionField(
											"msr_loan_borrowers_id",
											value || null,
										);
										// Reset loan selection when borrower changes
										updateReckoningsDeductionField(
											"msr_loans_id",
											null,
										);
									}}
								/>
							</div>
							<div style={{ gridColumn: "span 2" }}>
								<Label>Pożyczka</Label>
								<Select
									style={{ width: "100%" }}
									disabled={
										isDisabled || isLoanDeductionLocked
									}
									showSearch
									optionFilterProp="label"
									filterOption={(input, option) =>
										String(option?.label || "")
											.toLowerCase()
											.includes(
												String(
													input || "",
												).toLowerCase(),
											)
									}
									allowClear
									placeholder={
										effectiveBorrowerId
											? "Wybierz pożyczkę"
											: "Wybierz pożyczkę (opcjonalnie wybierz osobę)"
									}
									value={deduction.msr_loans_id || undefined}
									options={filteredLoans}
									onChange={(value) => {
										updateReckoningsDeductionField(
											"msr_loans_id",
											value || null,
										);

										const nextLoan = loans.find(
											(x) =>
												String(x.value) ===
												String(value),
										);
										if (nextLoan?.msr_loan_borrowers_id) {
											updateReckoningsDeductionField(
												"msr_loan_borrowers_id",
												nextLoan.msr_loan_borrowers_id,
											);
										}
										const remaining = parseFloat(
											nextLoan?.msr_loans_remaining_amount ||
												0,
										);
										const available =
											getAvailableDeductionAmount();
										const suggested = Math.max(
											0,
											Math.min(
												available || 0,
												remaining || 0,
											),
										);

										if (
											value &&
											suggested > 0 &&
											(!deduction.msr_deductions_amount ||
												deduction.msr_deductions_amount ==
													0)
										) {
											updateReckoningsDeductionField(
												"msr_deductions_amount",
												Number(suggested.toFixed(2)),
											);
										}
									}}
								/>
							</div>
						</>
					) : null}
					<div style={{ gridColumn: "span 4" }}>
						<div style={labelStyle}>Komentarz odliczenia</div>
						<Input.TextArea
							disabled={isDisabled || isLoanDeductionLocked}
							value={deduction.msr_deductions_comment || ""}
							onChange={(e) =>
								updateReckoningsDeductionField(
									"msr_deductions_comment",
									e.target.value,
								)
							}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ReckoningsDeduction;
